import { ErrorFilterService } from '../services/error-filter.service';

/**
 * Interceptor para console.error y console.log que aplica filtrado ANTES de que DataDog capture los logs.
 * Esto es crítico porque DataDog tiene configurado forwardConsoleLogs: 'all' y captura
 * todos los console.error y console.log directamente, sin pasar por el ErrorHandler de Angular.
 *
 * Intercepta:
 * - console.error(...) - siempre aplica filtrado
 * - console.log(...) - aplica filtrado solo si contiene objetos Error
 */
export class ConsoleErrorInterceptor {
  private static originalConsoleError = console.error;
  private static originalConsoleLog = console.log;
  private static originalConsoleWarn = console.warn;
  // Exponer métodos originales para casos críticos (bootstrap errors)
  public static readonly getOriginalError = () => ConsoleErrorInterceptor.originalConsoleError;
  public static readonly getOriginalWarn = () => ConsoleErrorInterceptor.originalConsoleWarn;
  private static errorFilterService: ErrorFilterService | null = null;
  private static isIntercepted = false;
  private static initializationCount = 0;

  /**
   * Inicializa el interceptor de console.error y console.log.
   * DEBE llamarse ANTES de inicializar DataDog RUM para que el filtrado funcione.
   * @param errorFilterService Servicio de filtrado de errores
   */
  static initialize(errorFilterService: ErrorFilterService): void {
    // Prevenir múltiples llamadas de inicialización con diferentes instancias
    if (this.isIntercepted) {
      this.initializationCount++;
      // Solo usar la nueva instancia si es diferente y es la segunda vez
      if (this.errorFilterService !== errorFilterService && this.initializationCount === 2) {
        // Actualizar la referencia al servicio pero NO re-interceptar
        this.errorFilterService = errorFilterService;
      }
      return;
    }

    this.errorFilterService = errorFilterService;
    this.isIntercepted = true;

    // Guardar referencias a los métodos originales
    this.originalConsoleError = console.error.bind(console);
    this.originalConsoleLog = console.log.bind(console);
    this.originalConsoleWarn = console.warn.bind(console);
    this.initializationCount = 1;

    // Interceptar console.error
    console.error = (...args: any[]) => {
      // Verificar si es un error que debe ser filtrado
      if (this.shouldFilterConsoleOutput(args)) {
        // Error filtrado - no hacer nada, no enviar a DataDog
        // Opcionalmente, podemos registrar en desarrollo para debugging
        if (this.isDebugMode()) {
          this.originalConsoleError('[ConsoleErrorInterceptor] Filtered console.error:', ...args);
        }
        return;
      }

      // Error real - dejar que pase al console.error original
      this.originalConsoleError(...args);
    };

    // Interceptar console.log (solo filtra si contiene errores)
    console.log = (...args: any[]) => {
      // Solo filtrar si alguno de los argumentos es un Error
      const hasError = args.some(arg =>
        arg instanceof Error ||
        (arg && typeof arg === 'object' && (
          arg.stack ||
          arg.name === 'Error' ||
          (arg.message && arg.stack)
        ))
      );

      if (hasError && this.shouldFilterConsoleOutput(args)) {
        // Log con error filtrado - no enviar a DataDog
        if (this.isDebugMode()) {
          this.originalConsoleLog('[ConsoleErrorInterceptor] Filtered console.log with error:', ...args);
        }
        return;
      }

      // Log normal o con error real - dejar pasar
      this.originalConsoleLog(...args);
    };

    // Interceptar console.warn (filtrar solo si contiene errores)
    console.warn = (...args: any[]) => {
      // Verificar si algún argumento es un Error
      const hasError = args.some(arg =>
        arg instanceof Error ||
        (arg && typeof arg === 'object' && (
          arg.stack ||
          arg.name === 'Error' ||
          (arg.message && arg.stack)
        ))
      );

      // Solo filtrar warns que contengan errores
      if (hasError && this.shouldFilterConsoleOutput(args)) {
        if (this.isDebugMode()) {
          this.originalConsoleWarn('[ConsoleErrorInterceptor] Filtered console.warn with error:', ...args);
        }
        return;
      }

      // Warnings normales o de sistema pasan directo
      this.originalConsoleWarn(...args);
    };

    // Usar el método original para evitar que este log sea interceptado
    this.originalConsoleLog('[ConsoleErrorInterceptor] Inicializado para console.error, console.log y console.warn');
  }

  /**
   * Determina si una salida de consola debe ser filtrada
   * @param args Todos los argumentos del console
   * @returns true si debe ser filtrado, false si debe pasar
   */
  private static shouldFilterConsoleOutput(args: any[]): boolean {
    const firstArg = args[0];
    if (!this.errorFilterService) {
      // Si no hay servicio de filtrado, dejar pasar todo
      return false;
    }

    // Si el primer argumento es un Error object, usar el filtro existente
    if (firstArg instanceof Error) {
      return this.errorFilterService.shouldIgnoreError(firstArg);
    }

    // Si es un string que empieza con [ProductionError] o [Bootstrap Error], dejarlo pasar
    // porque ya fue procesado o es crítico para el arranque de la app
    if (typeof firstArg === 'string' &&
        (firstArg.includes('[ProductionError]') || firstArg.includes('[Bootstrap Error]'))) {
      return false;
    }

    // Buscar objetos Error en cualquier posición de los argumentos
    for (const arg of args) {
      if (arg instanceof Error && this.errorFilterService.shouldIgnoreError(arg)) {
        return true;
      }
    }

    // Concatenar todos los strings para buscar patrones
    const fullMessage = args
      .map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg?.message) return arg.message;
        if (arg?.error?.message) return arg.error.message;
        try {
          // Cada argumento necesita su propio WeakSet para detectar circularidad
          // correctamente dentro de ESE objeto específico
          const seen = new WeakSet();
          return JSON.stringify(arg, (_key, value) => {
            if (typeof value === 'object' && value !== null) {
              if (seen.has(value)) {
                return '[Circular]';
              }
              seen.add(value);
            }
            return value;
          });
        } catch {
          // Fallback seguro que no puede fallar
          return '[Object]';
        }
      })
      .join(' ');

    // Usar el filtro del servicio para evaluar el mensaje completo
    // Creamos un pseudo-error con el mensaje concatenado
    const pseudoError = { message: fullMessage };
    return this.errorFilterService.shouldIgnoreError(pseudoError);
  }

  /**
   * Restaura console.error y console.log originales (útil para testing)
   */
  static restore(): void {
    if (this.isIntercepted) {
      if (this.originalConsoleError) {
        console.error = this.originalConsoleError;
      }
      if (this.originalConsoleLog) {
        console.log = this.originalConsoleLog;
      }
      if (this.originalConsoleWarn) {
        console.warn = this.originalConsoleWarn;
      }
      this.isIntercepted = false;
      this.errorFilterService = null;
      this.initializationCount = 0;
      // Usar el original antes de restaurar para asegurar que se vea
      if (this.originalConsoleLog) {
        this.originalConsoleLog('[ConsoleErrorInterceptor] Restaurado a métodos de consola originales');
      }
    }
  }

  /**
   * Verifica si el interceptor está activo
   */
  static isActive(): boolean {
    return this.isIntercepted;
  }

  /**
   * Verifica si el modo debug está activo
   */
  private static isDebugMode(): boolean {
    // Angular 18 sin SSR - localStorage siempre disponible
    try {
      return localStorage.getItem('debug-error-filter') === 'true';
    } catch {
      // En caso de error (ej: localStorage bloqueado por políticas), retornar false
      return false;
    }
  }
}